import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  LinearProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Analytics as AnalyticsIcon,
  PlayArrow as ExecuteIcon,
  Stop as CancelIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  CloudDone as CompletedIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import {
  getCompatibleAnalysisTypes,
  getAnalysisTypeDetails,
  createAnalysis,
  executeAnalysis,
  CompatibleAnalysisType,
  AnalysisSettingsTemplate,
} from '../../utils/analysisApi';

interface CreateAnalysisModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onAnalysisCreated: () => void;
}

export const CreateAnalysisModal: React.FC<CreateAnalysisModalProps> = ({
  open,
  onClose,
  projectId,
  onAnalysisCreated,
}) => {
  const [step, setStep] = useState<'select' | 'configure' | 'submitted'>('select');
  const [selectedAnalysisType, setSelectedAnalysisType] = useState('');
  const [compatibleTypes, setCompatibleTypes] = useState<CompatibleAnalysisType[]>([]);
  const [settingsTemplate, setSettingsTemplate] = useState<AnalysisSettingsTemplate | null>(null);
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [settingsValid, setSettingsValid] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load compatible analysis types when modal opens
  useEffect(() => {
    if (open) {
      loadCompatibleTypes();
      resetForm();
    }
  }, [open, projectId]);

  // Real-time settings validation
  useEffect(() => {
    if (settingsTemplate && Object.keys(settings).length > 0) {
      validateSettingsRealTime();
    }
  }, [settings, settingsTemplate]);

  const resetForm = () => {
    setStep('select');
    setSelectedAnalysisType('');
    setCompatibleTypes([]);
    setSettingsTemplate(null);
    setSettings({});
    setSettingsValid(false);
    setValidationErrors([]);
    setError(null);
    setSubmitting(false);
    setSubmissionComplete(false);
    setAnalysisId(null);
  };

  const loadCompatibleTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCompatibleAnalysisTypes(projectId);
      setCompatibleTypes(response.compatible_types);
    } catch (err) {
      console.error('Error loading compatible analysis types:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analysis types');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalysisTypeDetails = async (analysisType: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAnalysisTypeDetails(analysisType);
      setSettingsTemplate(response.template);
      setSettings(response.template.default_settings);
    } catch (err) {
      console.error('Error loading analysis type details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analysis details');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalysisTypeSelect = async (analysisType: string) => {
    setSelectedAnalysisType(analysisType);
    await loadAnalysisTypeDetails(analysisType);
    setStep('configure');
  };

  const validateSettingsRealTime = () => {
    const errors: string[] = [];
    
    if (!settingsTemplate) {
      setSettingsValid(false);
      return;
    }

    // Validate min_conf (confidence threshold)
    const minConf = settings.min_conf;
    if (minConf === undefined || minConf === null || minConf === '') {
      errors.push('Confidence threshold is required');
    } else {
      const numValue = parseFloat(minConf);
      if (isNaN(numValue)) {
        errors.push('Confidence threshold must be a number');
      } else if (numValue < 0 || numValue > 1) {
        errors.push('Confidence threshold must be between 0 and 1');
      }
    }

    // Validate optional geographic coordinates
    if (settings.lat !== null && settings.lat !== undefined && settings.lat !== '') {
      const lat = parseFloat(settings.lat);
      if (isNaN(lat)) {
        errors.push('Latitude must be a valid number');
      } else if (lat < -90 || lat > 90) {
        errors.push('Latitude must be between -90 and 90');
      }
    }

    if (settings.lon !== null && settings.lon !== undefined && settings.lon !== '') {
      const lon = parseFloat(settings.lon);
      if (isNaN(lon)) {
        errors.push('Longitude must be a valid number');
      } else if (lon < -180 || lon > 180) {
        errors.push('Longitude must be between -180 and 180');
      }
    }

    // Validate optional week
    if (settings.week !== null && settings.week !== undefined && settings.week !== '' && settings.week !== -1) {
      const week = parseInt(settings.week);
      if (isNaN(week)) {
        errors.push('Week must be a valid number');
      } else if (week < 1 || week > 48) {
        errors.push('Week must be between 1 and 48 (or -1 for no filter)');
      }
    }

    // Validate overlap parameter if present
    if (settings.overlap !== null && settings.overlap !== undefined && settings.overlap !== '') {
      const overlap = parseFloat(settings.overlap);
      if (isNaN(overlap)) {
        errors.push('Overlap must be a valid number');
      } else if (overlap < 0 || overlap > 2.9) {
        errors.push('Overlap must be between 0 and 2.9');
      }
    }

    setValidationErrors(errors);
    setSettingsValid(errors.length === 0);
  };

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleCreateAndSubmitAnalysis = async () => {
    if (!selectedAnalysisType || !settingsValid) return;

    try {
      setSubmitting(true);
      setError(null);

      // Create Analysis
      const createResponse = await createAnalysis(projectId, {
        analysis_type: selectedAnalysisType,
        settings: settings,
      });

      const createdAnalysisId = createResponse.analysis.analysis_id;
      setAnalysisId(createdAnalysisId);

      // Show submission confirmation immediately - don't wait for execution
      setStep('submitted');
      setSubmissionComplete(true);

      // Start execution in background (don't wait for response)
      executeAnalysis(createdAnalysisId).catch(err => {
        console.error('Background execution failed:', err);
      });

    } catch (err) {
      console.error('Error creating analysis:', err);
      setError(err instanceof Error ? err.message : 'Failed to create analysis');
      setSubmitting(false);
    }
  };

  const renderAnalysisTypeSelection = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        Select Analysis Type
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose the type of analysis to run on your dataset
      </Typography>

      {compatibleTypes.length > 0 ? (
        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
          {compatibleTypes.map((type) => (
            <ListItem
              key={type.type}
              button
              onClick={() => type.compatible && handleAnalysisTypeSelect(type.type)}
              disabled={!type.compatible}
              sx={{
                border: '1px solid',
                borderColor: type.compatible ? 'grey.300' : 'grey.200',
                borderRadius: 2,
                mb: 1,
                opacity: type.compatible ? 1 : 0.6,
                '&:hover': {
                  bgcolor: type.compatible ? 'primary.50' : 'inherit',
                },
              }}
            >
              <ListItemIcon>
                <AnalyticsIcon color={type.compatible ? 'primary' : 'disabled'} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {type.description}
                    </Typography>
                    <Chip
                      label={type.compatible ? 'Compatible' : 'Not Compatible'}
                      color={type.compatible ? 'success' : 'error'}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {type.compatible 
                        ? `${type.compatible_files} of ${type.file_count} files compatible`
                        : type.reason
                      }
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Supports: {type.supported_file_extensions?.join(', ') || 'Multiple formats'}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Alert severity="warning">
          No compatible analysis types found for this project's dataset.
        </Alert>
      )}
    </Box>
  );

  const renderSettingsConfiguration = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Configure Analysis Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {settingsTemplate?.analysis_type.toUpperCase()} Analysis Configuration
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => setStep('select')}>
          Change Type
        </Button>
      </Box>

      {settingsTemplate && (
        <Box>
          {/* Core Settings */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Core Settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                {Object.entries(settingsTemplate.default_settings)
                  .filter(([key]) => ['confidence_threshold', 'sensitivity', 'min_conf'].includes(key))
                  .map(([key, defaultValue]) => (
                    <FormControl key={key} fullWidth>
                      <TextField
                        label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        type="number"
                        value={settings[key] || defaultValue}
                        onChange={(e) => handleSettingChange(key, parseFloat(e.target.value))}
                        inputProps={{
                          step: key === 'week' ? 1 : 0.1,
                          min: 0,
                          max: key.includes('conf') || key === 'sensitivity' ? 1 : undefined
                        }}
                        helperText={settingsTemplate.parameter_descriptions[key]}
                        disabled={submitting}
                      />
                    </FormControl>
                  ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Advanced Settings */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Advanced Settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                {Object.entries(settingsTemplate.default_settings)
                  .filter(([key]) => !['confidence_threshold', 'sensitivity', 'min_conf'].includes(key))
                  .map(([key, defaultValue]) => {
                    if (key === 'locale') {
                      return (
                        <FormControl key={key} fullWidth>
                          <InputLabel>Language</InputLabel>
                          <Select
                            value={settings[key] || defaultValue}
                            onChange={(e) => handleSettingChange(key, e.target.value)}
                            label="Language"
                            disabled={submitting}
                          >
                            <MenuItem value="en">English</MenuItem>
                            <MenuItem value="es">Spanish</MenuItem>
                            <MenuItem value="fr">French</MenuItem>
                            <MenuItem value="de">German</MenuItem>
                            <MenuItem value="it">Italian</MenuItem>
                          </Select>
                          <FormHelperText>{settingsTemplate.parameter_descriptions[key]}</FormHelperText>
                        </FormControl>
                      );
                    }
                    
                    return (
                      <FormControl key={key} fullWidth>
                        <TextField
                          label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          type={typeof defaultValue === 'number' ? 'number' : 'text'}
                          value={settings[key] ?? defaultValue}
                          onChange={(e) => handleSettingChange(
                            key, 
                            typeof defaultValue === 'number' ? parseFloat(e.target.value) : e.target.value
                          )}
                          helperText={settingsTemplate.parameter_descriptions[key]}
                          disabled={submitting}
                        />
                      </FormControl>
                    );
                  })}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Location Settings */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Location Filtering (Optional)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" gap={2}>
                  <TextField
                    label="Latitude"
                    type="number"
                    value={settings.lat || ''}
                    onChange={(e) => handleSettingChange('lat', e.target.value ? parseFloat(e.target.value) : null)}
                    inputProps={{ step: 0.000001, min: -90, max: 90 }}
                    helperText="Latitude (-90 to 90)"
                    fullWidth
                    disabled={submitting}
                  />
                  <TextField
                    label="Longitude"
                    type="number"
                    value={settings.lon || ''}
                    onChange={(e) => handleSettingChange('lon', e.target.value ? parseFloat(e.target.value) : null)}
                    inputProps={{ step: 0.000001, min: -180, max: 180 }}
                    helperText="Longitude (-180 to 180)"
                    fullWidth
                    disabled={submitting}
                  />
                </Box>
                <TextField
                  label="Week of Year"
                  type="number"
                  value={settings.week || -1}
                  onChange={(e) => handleSettingChange('week', parseInt(e.target.value))}
                  inputProps={{ min: -1, max: 48 }}
                  helperText="Week 1-48 for seasonal filtering, -1 for no filter"
                  fullWidth
                  disabled={submitting}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* Real-time Validation Results */}
      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }} icon={<ErrorIcon />}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Please fix the following issues:
          </Typography>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            {validationErrors.map((error, index) => (
              <li key={index}>
                <Typography variant="body2">{error}</Typography>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {settingsValid && !submitting && (
        <Alert severity="success" sx={{ mt: 2 }} icon={<CheckIcon />}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Settings are valid and ready for submission
          </Typography>
        </Alert>
      )}
    </Box>
  );

  const renderSubmissionConfirmation = () => (
    <Box textAlign="center" py={4}>
      <ScheduleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        Analysis Submitted Successfully!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Your {selectedAnalysisType.toUpperCase()} analysis has been submitted and will run independently in the background.
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          What happens next:
        </Typography>
        <Typography variant="body2">
          - Analysis is running on cloud infrastructure<br/>
          - You can close your browser or work on other projects<br/>
          - Progress updates will appear on your project dashboard<br/>
          - Processing may take 30 minutes to 2 hours depending on dataset size<br/>
          - You'll be able to download results when complete
        </Typography>
      </Alert>

      {analysisId && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          Analysis ID: {analysisId}
        </Typography>
      )}
      
    </Box>
  );

  const getStepTitle = () => {
    switch (step) {
      case 'select': return 'Select Analysis Type';
      case 'configure': return 'Configure Analysis Settings';
      case 'submitted': return 'Analysis Submitted';
      default: return 'Create Analysis';
    }
  };

  const canCloseModal = () => {
    return !submitting;
  };

  return (
    <Dialog
      open={open}
      onClose={canCloseModal() ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: 600 } }}
      disableEscapeKeyDown={!canCloseModal()}
    >
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {getStepTitle()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {step === 'submitted' ? 
            'Your analysis is now running in the background' :
            'Configure and submit analysis for your project dataset'
          }
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* Progress Indicator */}
        {step !== 'submitted' && (
          <Box mb={3}>
            <LinearProgress 
              variant="determinate" 
              value={step === 'select' ? 50 : 100}
              sx={{ height: 4, borderRadius: 1 }}
            />
            <Box display="flex" justifyContent="space-between" mt={1}>
              <Typography variant="caption" color={step === 'select' ? 'primary' : 'text.secondary'}>
                Select Type
              </Typography>
              <Typography variant="caption" color={step === 'configure' ? 'primary' : 'text.secondary'}>
                Configure Settings
              </Typography>
            </Box>
          </Box>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Step Content */}
        {step === 'select' && renderAnalysisTypeSelection()}
        {step === 'configure' && renderSettingsConfiguration()}
        {step === 'submitted' && renderSubmissionConfirmation()}
      </DialogContent>

      <DialogActions>
        {step !== 'submitted' && (
          <Button 
            onClick={onClose} 
            disabled={!canCloseModal()}
            sx={{ 
              opacity: !canCloseModal() ? 0.5 : 1,
              cursor: !canCloseModal() ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Submitting...' : 'Cancel'}
          </Button>
        )}
        
        {step === 'configure' && (
          <Button
            onClick={handleCreateAndSubmitAnalysis}
            disabled={!settingsValid || submitting}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : <ExecuteIcon />}
            sx={{ minWidth: 200 }}
          >
            {submitting ? 'Submitting Analysis...' : 'Submit Analysis'}
          </Button>
        )}

        {step === 'submitted' && (
          <Button
            onClick={() => {
              onAnalysisCreated();
              onClose();
            }}
            variant="contained"
            startIcon={<CheckIcon />}
          >
            Go to Project Dashboard
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};